import { useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { trpc } from "../../lib/api/trpc";
import { notify } from "../../utils/notify";
import { useCollections } from "../../features/documents/hooks";

type BindingRow = {
  id: string;
  kind: "CHANNEL" | "TEAM";
  bindingKey: string;
  displayName: string | null;
  /** name === null means the collection has since been deleted (#162). */
  collections: Array<{ id: string; name: string | null }>;
  createdAt: string;
};

/**
 * Admin mapping UI for Teams channel/team → collection bindings (#162).
 * Questions asked in a bound channel are answered only from the bound
 * collections; unbound channels stay org-wide.
 */
export function TeamsBindingsPage() {
  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, refetch } = trpc.teamsBindings.list.useQuery();
  const bindings = (data as BindingRow[]) || [];
  const { collections } = useCollections();

  const [showForm, setShowForm] = useState(false);
  const [kind, setKind] = useState<"CHANNEL" | "TEAM">("CHANNEL");
  const [bindingKey, setBindingKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // @ts-ignore - tRPC type inference through `as any` router cast
  const createMutation = trpc.teamsBindings.create.useMutation({
    onSuccess: () => {
      refetch();
      setShowForm(false);
      setBindingKey("");
      setDisplayName("");
      setSelectedIds([]);
      notify.success("Binding created");
    },
    onError: (error: { message?: string }) => {
      notify.error(error.message || "Failed to create binding");
    },
  });

  // @ts-ignore - tRPC type inference through `as any` router cast
  const deleteMutation = trpc.teamsBindings.delete.useMutation({
    onSuccess: () => refetch(),
    onError: (error: { message?: string }) => {
      notify.error(error.message || "Failed to delete binding");
    },
  });

  const toggleCollection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleCreate = () => {
    createMutation.mutate({
      kind,
      bindingKey: bindingKey.trim(),
      displayName: displayName.trim() || undefined,
      collectionIds: selectedIds,
    });
  };

  const handleDelete = (binding: BindingRow) => {
    if (
      window.confirm(
        `Remove the binding for "${binding.displayName || binding.bindingKey}"? Questions in that ${binding.kind === "TEAM" ? "team" : "channel"} will be answered from all documents again.`,
      )
    ) {
      deleteMutation.mutate({ id: binding.id });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Teams Channel Bindings
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            Map a Teams channel (or a whole team) to collections. Questions
            asked to the bot in a bound channel are answered only from those
            collections — no user action needed. Unbound channels answer from
            all documents.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong transition-colors flex-shrink-0"
        >
          <PlusIcon className="h-4 w-4" />
          Add binding
        </button>
      </div>

      {showForm && (
        <div className="bg-surface border border-subtle rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Binding type
              </label>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as "CHANNEL" | "TEAM")}
                className="w-full px-3 py-2 text-sm bg-surface border border-subtle rounded-lg focus:border-accent focus:outline-none text-text-base"
              >
                <option value="CHANNEL">Channel</option>
                <option value="TEAM">Whole team</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Label (optional)
              </label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. #twist-support"
                maxLength={100}
                className="w-full px-3 py-2 text-sm bg-surface border border-subtle rounded-lg focus:border-accent focus:outline-none text-text-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              {kind === "TEAM" ? "Team ID" : "Channel ID"}
            </label>
            <input
              value={bindingKey}
              onChange={(e) => setBindingKey(e.target.value)}
              placeholder="19:xxxxxxxx@thread.tacv2"
              className="w-full px-3 py-2 text-sm font-mono bg-surface border border-subtle rounded-lg focus:border-accent focus:outline-none text-text-base"
            />
            <p className="text-xs text-text-faint mt-1">
              In Teams: channel menu → “Get link to channel” — the ID is the
              part starting with 19: (URL-decoded) in the link.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Collections
            </label>
            {collections.length === 0 ? (
              <p className="text-sm text-text-faint">
                No collections yet — create one on the Documents page first.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {collections.map((c) => (
                  <label
                    key={c.id}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors ${
                      selectedIds.includes(c.id)
                        ? "border-accent bg-accent-tint text-accent"
                        : "border-subtle text-text-muted hover:bg-surface-2"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleCollection(c.id)}
                      className="sr-only"
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowForm(false)}
              className="px-3 py-2 text-sm font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={
                !bindingKey.trim() ||
                selectedIds.length === 0 ||
                createMutation.isLoading
              }
              className="px-3 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isLoading ? "Creating…" : "Create binding"}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
      ) : bindings.length === 0 ? (
        <div className="bg-surface border border-subtle rounded-lg p-8 text-center">
          <p className="text-sm text-text-faint">
            No bindings yet. The bot answers every channel from all documents.
          </p>
        </div>
      ) : (
        <div className="bg-surface border border-subtle rounded-lg divide-y divide-subtle">
          {bindings.map((b) => (
            <div key={b.id} className="flex items-center gap-4 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-base truncate">
                    {b.displayName || b.bindingKey}
                  </span>
                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-2 text-text-faint">
                    {b.kind === "TEAM" ? "Team" : "Channel"}
                  </span>
                </div>
                {b.displayName && (
                  <p className="text-xs font-mono text-text-faint truncate">
                    {b.bindingKey}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 justify-end">
                {b.collections.map((c) =>
                  c.name ? (
                    <span
                      key={c.id}
                      className="inline-flex px-2 py-0.5 rounded text-xs bg-accent-tint text-accent"
                    >
                      {c.name}
                    </span>
                  ) : (
                    <span
                      key={c.id}
                      className="inline-flex px-2 py-0.5 rounded text-xs bg-warn-bg text-warn"
                      title="This collection was deleted — questions in this channel will fail until the binding is updated."
                    >
                      Deleted collection
                    </span>
                  ),
                )}
              </div>
              <button
                onClick={() => handleDelete(b)}
                disabled={deleteMutation.isLoading}
                className="p-1.5 text-text-faint hover:text-danger rounded-lg hover:bg-surface-2 transition-colors"
                aria-label={`Delete binding ${b.displayName || b.bindingKey}`}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
